import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// ─── Changelog data ────────────────────────────────────────────────────────
const releases = [
  {
    version: "v260713.1218",
    date: "Jul 13, 2026",
    label: "Performance Pass",
    tags: ["performance"],
    changes: [
      {
        type: "perf",
        text: "Set 1-year browser Cache-Control headers on all S3 images — repeat visits now load from cache instantly",
      },
      {
        type: "perf",
        text: "Added preconnect + DNS prefetch to S3 asset host",
      },
      { type: "perf", text: "Preload first hero image (LCP) in page head" },
      {
        type: "perf",
        text: "Added fetchPriority='high' to first hero slide and decoding='async' to lightbox images",
      },
    ],
  },
  {
    version: "v260713.1213",
    date: "Jul 13, 2026",
    label: "Share With Image",
    tags: ["feature"],
    changes: [
      {
        type: "feat",
        text: "Fixed iOS Safari sticker share — replaced fetch(dataUrl) with reliable atob conversion",
      },
      {
        type: "feat",
        text: "Facebook and X/Twitter share buttons now include the actual sticker image on mobile via native share API",
      },
      {
        type: "feat",
        text: "Random Albers social buttons (Facebook, X, LinkedIn) now attach the composition image on mobile",
      },
    ],
  },
  {
    version: "v260713.1210",
    date: "Jul 13, 2026",
    label: "Sticker UX + Alerts",
    tags: ["feature", "fix"],
    changes: [
      { type: "fix", text: "Mobile nav 'Contact Me' renamed to 'Contact'" },
      {
        type: "feat",
        text: "Sticker image file size limit increased from 290 KB to 360 KB — more room for detailed drawings",
      },
      {
        type: "feat",
        text: "Added popup alert modal when drawing without selecting a color first",
      },
      {
        type: "feat",
        text: "File-too-large error now shows a visible popup instead of inline text",
      },
    ],
  },
  {
    version: "v260711.1304",
    date: "Jul 11, 2026",
    label: "Theme Color Audit",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "Share button in sticker gallery now switches orange/teal with theme",
      },
      {
        type: "fix",
        text: "Checkout submit button now theme-aware (teal in dark, orange in light)",
      },
      {
        type: "fix",
        text: "'Order a print' link on Hello Stickers page now switches with theme",
      },
    ],
  },
  {
    version: "v260711.1300",
    date: "Jul 11, 2026",
    label: "DuckDuckGo Favicon Fix",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "Fixed favicon not switching colors in DuckDuckGo and Safari — now removes and recreates link elements on theme change",
      },
      {
        type: "fix",
        text: "Extended favicon update to cover apple-touch-icon links",
      },
    ],
  },
  {
    version: "v260711.1257",
    date: "Jul 11, 2026",
    label: "MS Paint Gallery Shuffle",
    tags: ["design"],
    changes: [
      {
        type: "design",
        text: "Reshuffled MS Paint gallery priority order to better interleave BMP/PNG/JPG types and spread white-background images",
      },
    ],
  },
  {
    version: "v260711.1251",
    date: "Jul 11, 2026",
    label: "Gallery Cleanup",
    tags: ["fix"],
    changes: [
      { type: "fix", text: "Removed IMG_1121.JPG from MS Paint gallery" },
    ],
  },
  {
    version: "v260711.1243",
    date: "Jul 11, 2026",
    label: "MS Paint Gallery Overhaul",
    tags: ["content"],
    changes: [
      {
        type: "content",
        text: "34 images renamed with descriptive filenames (freedom.png, soup.bmp, pigface.png, etc.)",
      },
      {
        type: "content",
        text: "19 new images added: sweater, pirateFriend, what1, dude, doodle, jim croce, centuar, bark, untitled, RAR, whaaa, new, the fam, grrr, fish fry, huh, eskimo, fredsavage, party",
      },
      { type: "content", text: "Gallery now at 100 images total" },
    ],
  },
  {
    version: "v260710.1504",
    date: "Jul 10, 2026",
    label: "Hello Stickers Theme Fix",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "'Back to Check in' button now switches orange/teal correctly with theme toggle",
      },
    ],
  },
  {
    version: "v260710.1502",
    date: "Jul 10, 2026",
    label: "Hero Heading Color",
    tags: ["design"],
    changes: [
      {
        type: "design",
        text: "'Now in the store' heading: teal in dark mode, light gray in light mode (was harsh orange)",
      },
    ],
  },
  {
    version: "v260710.1457",
    date: "Jul 10, 2026",
    label: "Solid Favicon",
    tags: ["design"],
    changes: [
      {
        type: "design",
        text: "Favicon simplified to solid flat circle — teal (#6cebe4) in dark, orange (#ff4000) in light",
      },
      {
        type: "fix",
        text: "Favicon now updates all icon link types simultaneously (icon, shortcut icon, apple-touch-icon)",
      },
    ],
  },
  {
    version: "v260710.1451",
    date: "Jul 10, 2026",
    label: "Theme-Switching Favicon",
    tags: ["feature"],
    changes: [
      {
        type: "feat",
        text: "Favicon now switches color on theme toggle — green/teal for dark mode, orange for light mode",
      },
    ],
  },
  {
    version: "v260710.1449",
    date: "Jul 10, 2026",
    label: "Store + Nav Polish",
    tags: ["fix", "design"],
    changes: [
      {
        type: "fix",
        text: "Store section shimmer dividers now stay correct color when toggling dark → light → dark",
      },
      {
        type: "fix",
        text: "'Contact Me' in mobile menu aligned to match Portfolio / About / Store links",
      },
    ],
  },
  {
    version: "v260710.1442",
    date: "Jul 10, 2026",
    label: "Contact Nav Link",
    tags: ["design"],
    changes: [
      {
        type: "design",
        text: "'Contact' in navbar changed from solid button to plain text link matching other nav items",
      },
    ],
  },
  {
    version: "v260710.1438",
    date: "Jul 10, 2026",
    label: "Favicon + Store Copy",
    tags: ["design", "content"],
    changes: [
      {
        type: "design",
        text: "Replaced pulsating favicon animation with static glowing green circle",
      },
      {
        type: "content",
        text: "Store page welcome text updated to mention unlisted pricing",
      },
    ],
  },
  {
    version: "v260709.1635",
    date: "Jul 9, 2026",
    label: "Google Analytics Fixed",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "Switched to correct GA4 measurement ID (G-9X2PXEEFFG) — previous ID was a Google Tag container with no configured destination",
      },
      {
        type: "fix",
        text: "Changed page_view tracking from gtag('config') to explicit gtag('event', 'page_view') for reliable SPA tracking",
      },
    ],
  },
  {
    version: "v260709.1528",
    date: "Jul 9, 2026",
    label: "Analytics Event Fix",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "send_page_view: false in gtag config was silently dropping all page view events — fixed with explicit event dispatch",
      },
    ],
  },
  {
    version: "v260709.1504",
    date: "Jul 9, 2026",
    label: "GA Measurement ID",
    tags: ["fix"],
    changes: [
      {
        type: "fix",
        text: "Updated measurement ID from G-CH9SWEK7NK (returning 404 from GTM) to G-E4DBHKLRZQ",
      },
    ],
  },
  {
    version: "v260706.0000",
    date: "Jul 6, 2026",
    label: "🚀 Site Launch",
    tags: ["launch"],
    changes: [
      {
        type: "feat",
        text: "miketerpeza.com goes live on AWS Elastic Beanstalk",
      },
      {
        type: "feat",
        text: "Portfolio, Store, About, Hello Stickers, and Random Albers pages live",
      },
      {
        type: "feat",
        text: "Dark/light theme toggle with localStorage persistence",
      },
      {
        type: "feat",
        text: "Hello Sticker drawing canvas with DynamoDB-backed gallery",
      },
      { type: "feat", text: "CloudFront CDN distribution active" },
    ],
  },
];

// ─── Tag config ──────────────────────────────────────────────────────────────
const tagConfig = {
  perf: {
    label: "Performance",
    color: "text-yellow-400 dark:text-yellow-300",
    bg: "bg-yellow-400/10",
    icon: "⚡",
  },
  feat: {
    label: "Feature",
    color: "text-emerald-400 dark:text-emerald-300",
    bg: "bg-emerald-400/10",
    icon: "✦",
  },
  fix: {
    label: "Fix",
    color: "text-sky-400 dark:text-sky-300",
    bg: "bg-sky-400/10",
    icon: "◈",
  },
  design: {
    label: "Design",
    color: "text-purple-400 dark:text-purple-300",
    bg: "bg-purple-400/10",
    icon: "◎",
  },
  content: {
    label: "Content",
    color: "text-orange-400 dark:text-orange-300",
    bg: "bg-orange-400/10",
    icon: "◆",
  },
  launch: {
    label: "Launch",
    color: "text-pink-400 dark:text-pink-300",
    bg: "bg-pink-400/10",
    icon: "★",
  },
};

const releaseTagConfig = {
  performance: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
  feature: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  fix: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  design: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  content: "bg-orange-400/15 text-orange-300 border-orange-400/30",
  launch: "bg-pink-400/15 text-pink-300 border-pink-400/30",
};

// ─── Retro terminal renderer ─────────────────────────────────────────────────
function RetroChangelog() {
  const [visibleLines, setVisibleLines] = useState(0);

  const lines = [];
  lines.push(
    "  ███╗   ███╗ ██╗ ██╗  ██╗███████╗  ████████╗███████╗██████╗ ██████╗ ███████╗███████╗ █████╗ ",
  );
  lines.push(
    "  ████╗ ████║ ██║ ██║ ██╔╝██╔════╝  ╚══██╔══╝██╔════╝██╔══██╗██╔══██╗██╔════╝╚════██║██╔══██╗",
  );
  lines.push(
    "  ██╔████╔██║ ██║ █████╔╝ █████╗       ██║   █████╗  ██████╔╝██████╔╝█████╗      ██╔╝███████║",
  );
  lines.push(
    "  ██║╚██╔╝██║ ██║ ██╔═██╗ ██╔══╝       ██║   ██╔══╝  ██╔══██╗██╔═══╝ ██╔══╝     ██╔╝ ██╔══██║",
  );
  lines.push(
    "  ██║ ╚═╝ ██║ ██║ ██║  ██╗███████╗     ██║   ███████╗██║  ██║██║     ███████╗   ██║  ██║  ██║",
  );
  lines.push(
    "  ╚═╝     ╚═╝ ╚═╝ ╚═╝  ╚═╝╚══════╝     ╚═╝   ╚══════╝╚═╝  ╚═╝╚═╝     ╚══════╝   ╚═╝  ╚═╝  ╚═╝",
  );
  lines.push("");
  lines.push("  [ miketerpeza.com // release log // artdept v1 ]");
  lines.push("  ──────────────────────────────────────────────────");
  lines.push("");

  releases.forEach((r) => {
    lines.push(`  > ${r.version}  ·  ${r.date}  ·  ${r.label.toUpperCase()}`);
    r.changes.forEach((c) => {
      lines.push(`    ${tagConfig[c.type]?.icon || "·"}  ${c.text}`);
    });
    lines.push("");
  });

  lines.push("  ──────────────────────────────────────────────────");
  lines.push("  [ END OF LOG ]  /  miketerpeza.com");
  lines.push("");

  useEffect(() => {
    setVisibleLines(0);
    let i = 0;
    const timer = setInterval(() => {
      i += 2;
      setVisibleLines(i);
      if (i >= lines.length) clearInterval(timer);
    }, 18);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-green-400 font-mono py-12 px-4 sm:px-8 overflow-x-auto"
      style={{ textShadow: "0 0 8px rgba(74,222,128,0.8)" }}
    >
      <style>{`
        @keyframes scanline {
          0% { background-position: 0 0; }
          100% { background-position: 0 100%; }
        }
        .crt-overlay {
          pointer-events: none;
          position: fixed;
          inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            transparent 0px,
            transparent 3px,
            rgba(0,0,0,0.07) 3px,
            rgba(0,0,0,0.07) 4px
          );
          z-index: 9999;
        }
      `}</style>
      <div className="crt-overlay" aria-hidden />

      <div className="max-w-5xl mx-auto">
        <div className="mb-6 text-xs text-green-600 tracking-widest uppercase">
          <Link to="/" className="hover:text-green-400 transition-colors">
            ← back to site
          </Link>
        </div>

        {lines.slice(0, visibleLines).map((line, i) => (
          <pre
            key={i}
            className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words"
          >
            {line || " "}
          </pre>
        ))}

        {visibleLines < lines.length && (
          <span
            className="inline-block w-2 h-4 bg-green-400 animate-pulse ml-1"
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}

// ─── Modern changelog renderer ───────────────────────────────────────────────
function ModernChangelog({ isDark }) {
  const accent = isDark ? "#6cebe4" : "#ff4000";
  const [visible, setVisible] = useState(new Set());

  useEffect(() => {
    const timers = releases.map((r, i) =>
      setTimeout(() => setVisible((prev) => new Set([...prev, i])), i * 60),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <Link
            to="/"
            className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-4 inline-block"
          >
            ← miketerpeza.com
          </Link>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tight"
            style={{ color: accent }}
          >
            Release Notes
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {releases.length} builds &nbsp;·&nbsp; miketerpeza.com v1
            &nbsp;·&nbsp; artdept
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="relative">
          {/* Vertical line */}
          <div
            className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-700 to-transparent"
            aria-hidden
          />

          <div className="space-y-8">
            {releases.map((release, i) => (
              <div
                key={release.version}
                className="relative pl-8 transition-all duration-500"
                style={{
                  opacity: visible.has(i) ? 1 : 0,
                  transform: visible.has(i)
                    ? "translateY(0)"
                    : "translateY(12px)",
                }}
              >
                {/* Timeline dot */}
                <div
                  className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-gray-950"
                  style={{ backgroundColor: accent }}
                  aria-hidden
                />

                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md dark:hover:shadow-gray-800/50 transition-shadow overflow-hidden">
                  {/* Card header */}
                  <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="font-mono text-xs font-bold px-2 py-0.5 rounded-md"
                        style={{
                          backgroundColor: `${accent}20`,
                          color: accent,
                        }}
                      >
                        {release.version}
                      </span>
                      {release.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border ${releaseTagConfig[tag] || "bg-gray-100 text-gray-500 border-gray-200"}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                      {release.date}
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="px-5 py-4">
                    <h2 className="text-base font-bold mb-3 text-gray-800 dark:text-gray-100">
                      {release.label}
                    </h2>
                    <ul className="space-y-2">
                      {release.changes.map((change, j) => {
                        const cfg = tagConfig[change.type] || tagConfig.feat;
                        return (
                          <li
                            key={j}
                            className="flex items-start gap-2.5 text-sm"
                          >
                            <span
                              className={`mt-0.5 flex-shrink-0 text-base leading-none ${cfg.color}`}
                            >
                              {cfg.icon}
                            </span>
                            <span className="text-gray-600 dark:text-gray-300 leading-snug">
                              {change.text}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center text-xs text-gray-400 dark:text-gray-600 font-mono">
          miketerpeza.com &nbsp;·&nbsp; artdept v1 &nbsp;·&nbsp; all builds
          since launch
        </div>
      </div>
    </div>
  );
}

// ─── Page entry ───────────────────────────────────────────────────────────────
export default function Changelog() {
  const [isGreenScreen, setIsGreenScreen] = useState(() =>
    document.documentElement.classList.contains("green-screen"),
  );
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsGreenScreen(
        document.documentElement.classList.contains("green-screen"),
      );
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  if (isGreenScreen) return <RetroChangelog />;
  return <ModernChangelog isDark={isDark} />;
}
